import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import HomePage from './pages/HomePage';
import NewThinkingSession from './pages/NewThinkingSession';
import ThinkLabPopup from './pages/ThinkLabPopup';
import SessionsList from './pages/SessionsList';
import HowItWorks from './pages/HowItWorks';
import UserGuide from './pages/UserGuide';
import AboutUs from './pages/AboutUs';
import FeedbackDashboard from './pages/FeedbackDashboard';
import AdminDashboard from './pages/AdminDashboard';
import Dashboard from './pages/Dashboard';
import Pricing from './pages/Pricing';
import Challenges from './pages/Challenges';
import ChallengeDetail from './pages/ChallengeDetail';
import QuickEvaluate from './pages/QuickEvaluate';
import MyEvaluations from './pages/MyEvaluations';
import FeatureGallery from './pages/FeatureGallery';
import MyProfile from './pages/MyProfile';
import PublicProfile from './pages/PublicProfile';
import PublicEvaluation from './pages/PublicEvaluation';
import EditProfile from './pages/EditProfile';
import CommunityPage from './pages/CommunityPage';
import CreateChallenge from './pages/CreateChallenge';
import CreateImpactLab from './pages/CreateImpactLab';
import ImpactLabAnalysis from './pages/ImpactLabAnalysis';
import ImpactLabDashboard from './pages/ImpactLabDashboard';
import TeamDashboard from './pages/TeamDashboard';
import CreateTeam from './pages/CreateTeam';
import AchievementsPage from './pages/AchievementsPage';
import MyWorkPage from './pages/MyWorkPage';
import InviteLanding from './pages/InviteLanding';
import Auth from './pages/Auth';
import AuthMagicLink from './pages/AuthMagicLink';
import ForgotPassword from './pages/ForgotPassword';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import EducatorDashboard from './pages/EducatorDashboard';
import CohortDetail from './pages/CohortDetail';
import BatchEvaluationWizard from './pages/BatchEvaluationWizard';
import SecuritySettings from './pages/SecuritySettings';
import { Toaster } from './components/ui/toaster';
import { AuthProvider } from './contexts/AuthContext';
import { PersonaProvider } from './contexts/PersonaContext';
import { WorkRefreshProvider } from './contexts/WorkRefreshContext';
import PersonaModal from './components/PersonaModal';
import FloatingActionButtons from './components/FloatingActionButtons';
import ProtectedRoute from './components/ProtectedRoute';
import GlobalErrorHandler from './components/GlobalErrorHandler';
import Onboarding, { useOnboarding } from './components/Onboarding';
import UnifiedSidebar from './components/UnifiedSidebar';

// Wrapper component that shows onboarding for new users (only on dashboard)
const AppWithOnboarding = ({ children }) => {
  const { showOnboarding, completeOnboarding } = useOnboarding();
  const location = window.location.pathname;
  
  // Only show onboarding on dashboard, not on auth pages or homepage
  const shouldShowOnboarding = showOnboarding && location === '/dashboard';
  
  return (
    <>
      {shouldShowOnboarding && (
        <Onboarding 
          onComplete={completeOnboarding} 
          onSkip={completeOnboarding} 
        />
      )}
      {children}
    </>
  );
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <PersonaProvider>
          <WorkRefreshProvider>
          <GlobalErrorHandler>
          <BrowserRouter>
            <AppWithOnboarding>
            <UnifiedSidebar>
            <Routes>
              <Route path="/" element={<HomePage />} />
              
              {/* Consolidated auth routes - all point to AuthMagicLink */}
              <Route path="/auth" element={<Navigate to="/login" replace />} />
              <Route path="/auth/verify" element={<AuthMagicLink />} />
              <Route path="/login" element={<AuthMagicLink />} />
              <Route path="/signup" element={<Navigate to="/login" replace />} />
              <Route path="/auth-legacy" element={<Navigate to="/login" replace />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/user-guide" element={<UserGuide />} />
              <Route path="/about" element={<AboutUs />} />
              <Route path="/feedback-dashboard" element={<FeedbackDashboard />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/achievements" element={<AchievementsPage />} />
              
              {/* Invite landing page (public) */}
              <Route path="/invite/:inviteCode" element={<InviteLanding />} />
              
              {/* Public evaluation view (shareable) */}
              <Route path="/evaluation/:evaluationId" element={<PublicEvaluation />} />
              
              {/* Pricing hidden until 50K users */}
              {/* <Route path="/pricing" element={<Pricing />} /> */}
              <Route path="/features" element={<FeatureGallery />} />
              <Route path="/challenges" element={<Challenges />} />
              <Route path="/challenges/create" element={
                <ProtectedRoute featureName="Create Challenge">
                  <CreateChallenge />
                </ProtectedRoute>
              } />
              <Route path="/impact-lab/create" element={
                <ProtectedRoute featureName="Start Impact Lab">
                  <CreateImpactLab />
                </ProtectedRoute>
              } />
              <Route path="/challenges/:challengeId/impact-lab" element={
                <ProtectedRoute featureName="Impact Lab">
                  <ImpactLabAnalysis />
                </ProtectedRoute>
              } />
              <Route path="/impact-lab/:labId" element={
                <ProtectedRoute featureName="Impact Lab Dashboard">
                  <ImpactLabDashboard />
                </ProtectedRoute>
              } />
              
              {/* Protected Routes - Require Authentication */}
              <Route path="/dashboard" element={
                <ProtectedRoute featureName="Dashboard">
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/my-work" element={
                <ProtectedRoute featureName="My Work">
                  <MyWorkPage />
                </ProtectedRoute>
              } />
              <Route path="/challenge/:challengeId" element={
                <ProtectedRoute featureName="Challenge Submissions">
                  <ChallengeDetail />
                </ProtectedRoute>
              } />
              <Route path="/quick-evaluate" element={
                <ProtectedRoute featureName="Quick Evaluate">
                  <QuickEvaluate />
                </ProtectedRoute>
              } />
              <Route path="/evaluate" element={<Navigate to="/quick-evaluate" replace />} />
              <Route path="/my-evaluations" element={
                <ProtectedRoute featureName="My Evaluations">
                  <MyEvaluations />
                </ProtectedRoute>
              } />
              <Route path="/profile" element={
                <ProtectedRoute featureName="Profile">
                  <MyProfile />
                </ProtectedRoute>
              } />
              <Route path="/profile/edit" element={
                <ProtectedRoute featureName="Edit Profile">
                  <EditProfile />
                </ProtectedRoute>
              } />
              <Route path="/profile/:username" element={<PublicProfile />} />
              <Route path="/community" element={<CommunityPage />} />
              <Route path="/teams/dashboard" element={
                <ProtectedRoute featureName="Team Dashboard">
                  <TeamDashboard />
                </ProtectedRoute>
              } />
              <Route path="/teams/create" element={
                <ProtectedRoute featureName="Create Team">
                  <CreateTeam />
                </ProtectedRoute>
              } />

              {/* IF Session - Guest accessible for testing */}
              <Route path="/session/new" element={<NewThinkingSession />} />
              <Route path="/new-session" element={<NewThinkingSession />} />
              <Route path="/session/:id" element={<NewThinkingSession />} />
              <Route path="/think-lab-popup" element={
                <ProtectedRoute featureName="Think Lab">
                  <ThinkLabPopup />
                </ProtectedRoute>
              } />
              <Route path="/sessions" element={
                <ProtectedRoute featureName="Past Sessions">
                  <SessionsList />
                </ProtectedRoute>
              } />
              
              {/* Educator Module */}
              <Route path="/educator" element={
                <ProtectedRoute featureName="Educator Dashboard">
                  <EducatorDashboard />
                </ProtectedRoute>
              } />
              <Route path="/educator/cohort/:cohortId" element={
                <ProtectedRoute featureName="Cohort Details">
                  <CohortDetail />
                </ProtectedRoute>
              } />
              <Route path="/educator/cohort/:cohortId/batch" element={
                <ProtectedRoute featureName="Batch Evaluation">
                  <BatchEvaluationWizard />
                </ProtectedRoute>
              } />
              
              {/* Security Settings */}
              <Route path="/settings/security" element={
                <ProtectedRoute featureName="Security Settings">
                  <SecuritySettings />
                </ProtectedRoute>
              } />
            </Routes>
            </UnifiedSidebar>
            </AppWithOnboarding>
            <Toaster />
            <PersonaModal />
            <FloatingActionButtons />
          </BrowserRouter>
          </GlobalErrorHandler>
          </WorkRefreshProvider>
        </PersonaProvider>
      </AuthProvider>
    </div>
  );
}

export default App;